import { motion } from "framer-motion";
import { Download, Star, Calendar, User, ChevronRight } from "lucide-react";
import { portStyle, formatDownloads } from "@/lib/api";
import { TitleAnimation } from "@/components/TitleAnimation";

export const HeroShowcase = ({ mod, onDownload, onView }) => {
  if (!mod) return null;

  return (
    <section data-testid="hero-showcase" className="relative">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative overflow-hidden rounded-xl border border-[#2e2a36] grain scanlines bg-[#121115]"
      >
        <div className="absolute inset-0">
          <img
            src={mod.cover_image}
            alt={mod.title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c] via-[#0a0a0c]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-transparent" />
        </div>

        <div className="relative z-30 px-6 sm:px-10 lg:px-14 py-10 sm:py-16 max-w-2xl">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 border border-[#ef4444]/50 bg-[#ef4444]/10 rounded-full">
            <Star className="w-3.5 h-3.5 text-[#ef4444] fill-[#ef4444]" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#f87171]">
              Doomhead's Choice
            </span>
          </div>

          <h1 className="font-pixel text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-tight mb-4 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] flex items-center gap-3 flex-wrap">
            <TitleAnimation mod={mod} size={48} />
            <span>{mod.title}</span>
          </h1>

          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6 font-body max-w-xl">
            {mod.tagline}
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-6 text-xs font-mono text-gray-400">
            <span className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-[#84cc16]" /> {mod.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-[#84cc16]" /> {mod.year}
            </span>
            <span className="flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5 text-[#f59e0b] fill-[#f59e0b]" />
              {mod.rating.toFixed(1)}
            </span>
            <span className="flex items-center gap-1.5 text-[#f59e0b]">
              <Download className="w-3.5 h-3.5" />
              {formatDownloads(mod.downloads)}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {mod.source_ports.map((p) => {
              const s = portStyle(p);
              return (
                <span
                  key={p}
                  className="px-2.5 py-1 rounded font-mono text-[10px] tracking-wider uppercase border"
                  style={{ background: s.bg, borderColor: s.border, color: s.text }}
                >
                  {p}
                </span>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              data-testid="hero-download-btn"
              onClick={() => onDownload(mod)}
              className="group inline-flex items-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white font-pixel text-xs uppercase tracking-wider px-6 py-3.5 rounded-md transition-all hover:shadow-[0_0_24px_rgba(239,68,68,0.5)]"
            >
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              Direct Download
            </button>
            <button
              data-testid="hero-details-btn"
              onClick={() => onView(mod)}
              className="inline-flex items-center gap-1.5 border border-[#2e2a36] hover:border-[#84cc16] text-gray-200 hover:text-[#84cc16] font-mono text-xs uppercase tracking-wider px-5 py-3.5 rounded-md transition-colors bg-[#18161d]/60"
            >
              View Details
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
